import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import { CheckCircle, WarningOctagon, Warning, Info, X } from '@phosphor-icons/react';
import type { ComponentChildren } from 'preact';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  leaving?: boolean;
}

interface ToastContextValue {
  show: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ show: () => {} });

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

const ICONS = {
  success: CheckCircle,
  error: WarningOctagon,
  warning: Warning,
  info: Info,
};

let nextId = 0;

export function ToastProvider({ children }: { children: ComponentChildren }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 300);
  }, []);

  const show = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++nextId;
    setToasts((prev) => {
      const next = [...prev, { id, message, type }];
      return next.length > 5 ? next.slice(-5) : next;
    });
    const timer = setTimeout(() => remove(id), 5000);
    timers.current.set(id, timer);
  }, [remove]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div class="toast-container">
        {toasts.map((toast) => {
          const Icon = ICONS[toast.type];
          return (
            <div key={toast.id} class={`toast ${toast.type}${toast.leaving ? ' leaving' : ''}`}>
              <span class="toast-icon"><Icon size={18} /></span>
              <span>{toast.message}</span>
              <button class="toast-close" onClick={() => remove(toast.id)}>
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
